import { useSyncExternalStore } from 'react';

/**
 * The surfaces, and the path that selects them.
 *
 * `App.tsx` says there is still no router, and there still is not one — six
 * console sections and a drill-down do not need it. What changed is that the
 * product now has more than one *audience*: a stranger reading a landing page,
 * somebody signing in, and an operator inside the console. Those are three
 * documents, not three tabs, and they need three URLs — a marketing page that
 * cannot be linked to is not a marketing page, and a sign-in form that shares
 * its URL with the landing page cannot be linked to from an email either.
 *
 * So this is the smallest thing that gives them addresses: a pathname, a
 * `pushState`, and a subscription. No dependency, because a router library at
 * this size would be scaffolding for a shape nobody has settled (ADR-0012 makes
 * the same argument about the Anthropic client). The moment the console itself
 * wants deep links — a tenant, a scan, a finding — that judgement is worth
 * revisiting, and this module is the thing to throw away when it is.
 *
 * **nginx already cooperates.** `try_files $uri $uri/ /index.html` means every
 * one of these paths serves the same document, so a reload or a pasted link
 * lands where it should rather than 404ing.
 */

export type Route =
  | { readonly name: 'landing' }
  | { readonly name: 'signin' }
  | { readonly name: 'terms' }
  | { readonly name: 'privacy' }
  | { readonly name: 'dpa' }
  | { readonly name: 'console' }
  | { readonly name: 'admin' };

export const LANDING_PATH = '/';
export const SIGN_IN_PATH = '/signin';
export const CONSOLE_PATH = '/app';

/**
 * Three legal documents at three addresses, and the plural is the decision.
 *
 * A due-diligence questionnaire asks for "a link to your privacy policy"; a
 * procurement portal wants the data processing agreement on its own, pasted
 * into a field that will be read by somebody who never sees the rest of this
 * site. Neither of those can be answered with a link to a page of tabs — the
 * recipient lands on the terms and has to be told which tab to press, in an
 * exchange where every extra instruction is a reason to come back and ask.
 * That is ADR-0037's argument for sign-in having an address, applied to the
 * three documents that get quoted at us in writing.
 *
 * They are public in the strict sense: no session, no API call, nothing that
 * can fail for a stranger. A legal page that only renders for somebody who has
 * signed in is not published, and being asked to accept terms you cannot read
 * from where you are being asked is the specific failure this avoids.
 */
export const TERMS_PATH = '/terms';
export const PRIVACY_PATH = '/privacy';
export const DPA_PATH = '/dpa';

/**
 * The platform area: DriftWatch's own view of its customers.
 *
 * A fourth address rather than a section of `/app`, because it has a different
 * audience and a different vocabulary. Inside `/app`, "client" means a
 * Microsoft 365 tenant; here it means the firm paying us. Two meanings of the
 * same word on one screen is how somebody suspends the wrong thing, so they get
 * separate documents at separate URLs (ADR-0040).
 *
 * The address is not the guard. Every route behind it declares a requirement no
 * customer role can hold, and typing this path while signed in as a customer
 * produces a screen that says so rather than anything of ours.
 */
export const ADMIN_PATH = '/admin';

/**
 * The three legal documents, as a table rather than as three more `if`s.
 *
 * Each of them is matched by equality alone, which is safe here only because
 * the normalisation below runs first — the asymmetry that made `/signin/` show
 * marketing was three matches written three ways, and a table is the cheapest
 * way of not writing the next one differently again. Nothing hangs below these
 * addresses, so there is no prefix to match: `/privacy/anything` is a typo, and
 * a typo belongs on the page that explains what this is.
 */
const LEGAL_ROUTES: ReadonlyMap<string, Route> = new Map([
  [TERMS_PATH, { name: 'terms' } as const],
  [PRIVACY_PATH, { name: 'privacy' } as const],
  [DPA_PATH, { name: 'dpa' } as const],
]);

/**
 * Unknown paths resolve to the landing page rather than to a not-found screen.
 *
 * There is exactly one thing a stranger who mistyped a URL should be shown, and
 * it is the page that explains what this is. The console is deliberately *not*
 * the fallback: landing somebody on a sign-in prompt because of a typo tells
 * them they are locked out of something, which is both wrong and the worst
 * first impression available.
 */
const routeFor = (rawPathname: string): Route => {
  /*
   * A trailing slash is normalised away before anything is matched.
   *
   * `/app/` and `/admin/` already worked, because both are matched by prefix as
   * well as by equality; `/signin/` did not, because sign-in was matched by
   * equality alone. So two of the three addresses tolerated a trailing slash
   * and the third quietly showed the landing page instead — and sign-in is the
   * one address of the three that gets *linked to*, which is ADR-0037's whole
   * argument for giving it one. A mail client's autolinker adds the slash, a
   * content management system adds it, a person typing adds it, and nginx
   * serves the application either way. The visitor follows a link to sign in
   * and is shown a page that never mentions signing in.
   *
   * Normalising once here rather than adding a second equality test to the
   * sign-in line, because the bug was the asymmetry rather than that one
   * address: a surface added later would inherit the same trap. Three have been
   * since — the legal documents, which are the addresses most likely of all of
   * them to arrive with a slash on the end, because they are pasted into
   * questionnaires and procurement portals by hand.
   */
  const pathname = rawPathname.length > 1 ? rawPathname.replace(/\/+$/, '') : rawPathname;

  if (pathname === SIGN_IN_PATH) return { name: 'signin' };
  const legal = LEGAL_ROUTES.get(pathname);
  if (legal) return legal;
  if (pathname === ADMIN_PATH || pathname.startsWith(`${ADMIN_PATH}/`)) return { name: 'admin' };
  if (pathname === CONSOLE_PATH || pathname.startsWith(`${CONSOLE_PATH}/`)) {
    return { name: 'console' };
  }
  return { name: 'landing' };
};

/**
 * Which document the browser is showing, on the root element beside
 * `data-theme`.
 *
 * `styles.css` pins the console to a 1280px floor and releases it for the
 * public surfaces. That has to be true *before* the new surface paints — a
 * component effect runs after the first frame, which on a phone is one frame of
 * a landing page inside a 1280px viewport — so it is applied here, on the same
 * synchronous path as the navigation itself, rather than in a React effect.
 */
const applySurface = (route: Route): void => {
  // The platform area is a console-shaped document: dense tables, a 1280px
  // floor, nothing marketing about it.
  document.documentElement.setAttribute(
    'data-surface',
    route.name === 'console' || route.name === 'admin' ? 'console' : 'public',
  );
};

const listeners = new Set<() => void>();

const notify = (): void => {
  applySurface(routeFor(window.location.pathname));
  for (const listener of listeners) listener();
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * `useSyncExternalStore` compares snapshots with `Object.is`, so this has to
 * hand back the same string for the same location rather than a fresh object.
 * The route object is derived in the hook instead, where re-deriving it is free.
 */
const snapshot = (): string => window.location.pathname;

export const useRoute = (): Route => routeFor(useSyncExternalStore(subscribe, snapshot));

/**
 * Go somewhere, without a page load.
 *
 * `replace` is for the redirects rather than the clicks: a session that ended
 * and dropped somebody at sign-in should not leave the console in their back
 * button, because pressing it would put them straight back on a screen that
 * cannot load. Scroll is reset on every navigation for the reason it is in any
 * document-to-document move — arriving halfway down a page you have never seen
 * reads as a rendering fault.
 */
export const navigate = (to: string, options: { readonly replace?: boolean } = {}): void => {
  if (window.location.pathname === to) return;
  if (options.replace === true) window.history.replaceState({}, '', to);
  else window.history.pushState({}, '', to);
  window.scrollTo(0, 0);
  notify();
};

/**
 * Called once, before render. The browser's own back and forward buttons move
 * the URL without telling anybody, and a shell that does not listen for it
 * shows the previous screen at the new address.
 */
export const initRouting = (): void => {
  applySurface(routeFor(window.location.pathname));
  window.addEventListener('popstate', notify);
};
